import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Download } from "lucide-react";

const docTypeLabels = {
  id_document: "ID Document",
  proof_of_registration: "Proof of Registration",
  sponsorship_letter: "Sponsorship Letter",
  proof_of_address: "Proof of Address",
  other: "Other",
};

const statusColors = {
  uploaded: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
};

export default function AdminDocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [downloading, setDownloading] = useState(null);

  const fetchDocs = (status) => {
    setLoading(true);
    const params = status && status !== "all" ? { status } : {};
    api.get("/admin/documents", { params }).then((r) => setDocs(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchDocs(tab); }, [tab]);

  const updateStatus = async (docId, status, notes = "") => {
    try {
      await api.put(`/admin/documents/${docId}/status`, { status, admin_notes: notes });
      toast.success(`Document ${status}`);
      fetchDocs(tab);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const handleDownload = async (doc) => {
    setDownloading(doc._id);
    try {
      const resp = await api.get(`/documents/${doc._id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download document");
    } finally {
      setDownloading(null);
    }
  };

  const stats = {
    total: docs.length,
    pending: docs.filter((d) => d.status === "uploaded").length,
    approved: docs.filter((d) => d.status === "approved").length,
    rejected: docs.filter((d) => d.status === "rejected").length,
  };

  return (
    <DashboardLayout title="Document Review">
      <div className="space-y-6" data-testid="admin-documents-page">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total", value: stats.total },
            { label: "Pending", value: stats.pending },
            { label: "Approved", value: stats.approved },
            { label: "Rejected", value: stats.rejected },
          ].map((s) => (
            <Card key={s.label} className="border-border/60">
              <CardContent className="pt-6 text-center">
                <div className="font-heading text-2xl font-bold text-[#0A2540]">{s.value}</div>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
            <TabsTrigger value="uploaded" className="text-xs">Pending</TabsTrigger>
            <TabsTrigger value="approved" className="text-xs">Approved</TabsTrigger>
            <TabsTrigger value="rejected" className="text-xs">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>

        <Card className="border-border/60">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : docs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No documents found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docs.map((d) => (
                    <TableRow key={d._id} data-testid={`admin-doc-row-${d._id}`}>
                      <TableCell className="font-medium">{d.user_name}</TableCell>
                      <TableCell>{docTypeLabels[d.doc_type] || d.doc_type}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(d)} disabled={downloading === d._id} data-testid={`download-doc-${d._id}`}>
                          {downloading === d._id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                          {d.original_filename}
                        </Button>
                      </TableCell>
                      <TableCell><Badge className={statusColors[d.status]}>{d.status}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {d.status === "uploaded" && (
                            <>
                              <Button size="sm" variant="outline" className="text-xs h-7 text-emerald-700" onClick={() => updateStatus(d._id, "approved")} data-testid={`approve-doc-${d._id}`}>
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" className="text-xs h-7 text-red-700" onClick={() => updateStatus(d._id, "rejected", "Document does not meet requirements")} data-testid={`reject-doc-${d._id}`}>
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
